<template>
  <div class="viewer-wrapper">
    <div class="viewer-header">
      <h2 class="viewer-title">聊天數據查詢</h2>
      <b-button variant="outline-danger" size="sm" @click="logout">登出</b-button>
    </div>

    <div class="viewer-body">
      <b-form
        @submit.prevent="
          () => {
            currentPage = 1;
            searchChatData(true);
          }
        "
      >
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="角色名稱 (gameId)">
              <b-dropdown
                variant="outline-secondary"
                class="w-100 npc-dropdown"
                ref="npcDropdown"
                :text="selectedNpcText || '請輸入或選擇角色'"
              >
                <b-dropdown-form>
                  <b-form-input
                    v-model="npcSearch"
                    placeholder="輸入關鍵字搜尋..."
                    autocomplete="off"
                  />
                </b-dropdown-form>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item
                  v-for="(opt, idx) in filteredNpcOptions"
                  :key="idx"
                  @click="selectNpc(opt)"
                >
                  {{ opt.text }}
                </b-dropdown-item>
                <b-dropdown-text v-if="filteredNpcOptions.length === 0" class="text-muted">
                  沒有符合的角色
                </b-dropdown-text>
              </b-dropdown>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="使用者名稱 (userId)">
              <b-form-input
                v-model="chatDataQuery.userId"
                placeholder="例如: student01"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="開始時間">
              <b-form-input
                v-model="chatDataQuery.startTime"
                type="datetime-local"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="結束時間">
              <b-form-input
                v-model="chatDataQuery.endTime"
                type="datetime-local"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="mt-2">
          <b-col cols="12" md="6">
            <b-form-group label="輸入金額">
              <b-input-group append="1M tokens / 美金">
                <b-form-input
                  v-model="costInputAmount"
                  type="number"
                  min="0"
                  step="0.000001"
                  placeholder="請輸入每 1M tokens 單價"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="輸出金額">
              <b-input-group append="1M tokens / 美金">
                <b-form-input
                  v-model="costOutputAmount"
                  type="number"
                  min="0"
                  step="0.000001"
                  placeholder="請輸入每 1M tokens 單價"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col>
            <b-button
              type="submit"
              variant="primary"
              :disabled="isLoadingChatData"
              class="mr-2"
            >
              <b-spinner small v-if="isLoadingChatData"></b-spinner>
              {{ isLoadingChatData ? "查詢中..." : "查詢" }}
            </b-button>
            <b-button variant="secondary" @click="clearChatDataQuery" class="mr-2">
              清空
            </b-button>
            <b-button
              variant="info"
              @click="exportChatData($event)"
              :disabled="total === 0"
              class="mr-2"
            >
              匯出 CSV(原始資料)
            </b-button>
            <b-button
              variant="success"
              @click="exportExcelData($event)"
              :disabled="total === 0"
              class="mr-2"
            >
              匯出 EXCEL(整理後資料)
            </b-button>
            <b-button
              variant="warning"
              @click="calculateManualCost"
              class="mr-2"
            >
              試算
            </b-button>
            <span class="ml-2">
              費用參考連結：
              <a
                href="https://developers.openai.com/api/docs/pricing"
                target="_blank"
                rel="noopener noreferrer"
              >https://developers.openai.com/api/docs/pricing</a>
            </span>
            <span v-if="costCalculated" class="ml-2">
              試算結果：{{ costTotalAmount }} 美金
            </span>
            <div class="text-danger mt-2">
              試算僅供參考，實際請查詢 OpenAI 帳單。
            </div>
          </b-col>
        </b-row>
      </b-form>

      <b-card class="mt-4" v-if="chatDataResults.length > 0">
        <b-card-title>查詢結果（共 {{ total }} 筆）</b-card-title>
        <b-table
          :items="chatDataResults"
          :fields="chatDataFields"
          :current-page="currentPage"
          :per-page="pageSize"
          striped
          hover
          small
          responsive
        >
          <template #cell(timestamp)="ctx">
            {{ formatTimestampISO(ctx.value) }}
          </template>
          <template #cell(chatContent)="ctx">
            <span :title="ctx.value">
              {{
                ctx.value && ctx.value.length > 50
                  ? ctx.value.slice(0, 50) + "..."
                  : ctx.value
              }}
            </span>
          </template>
          <template #cell(tokenP)="ctx">
            {{ parseTokenCal(ctx.item.tokenCal, 'p') }}
          </template>
          <template #cell(tokenT)="ctx">
            {{ parseTokenCal(ctx.item.tokenCal, 't') }}
          </template>
        </b-table>

        <b-pagination
          v-if="total > pageSize"
          v-model="currentPage"
          :total-rows="total"
          :per-page="pageSize"
          align="center"
          @input="handlePageChange"
        />
      </b-card>

      <b-alert
        v-else-if="!isLoadingChatData && chatDataSearched"
        variant="warning"
        show
        class="mt-4"
      >
        沒有找到符合條件的聊天記錄
      </b-alert>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import * as XLSX from "xlsx";

export default {
  name: "ViewerSearch",
  data() {
    return {
      apiNetWork2: process.env.VUE_APP_API_BASE || "https://rwdevapi.ntustmeg.com",

      npcOptions: [],
      isLoadingNPCs: false,
      npcSearch: "",
      selectedNpcText: "",

      chatDataQuery: { gameId: "", userId: "", startTime: "", endTime: "" },

      isLoadingChatData: false,
      chatDataSearched: false,

      chatDataResults: [],
      chatDataFields: [
        { key: "timestamp", label: "時間", sortable: true },
        { key: "gameId", label: "GameID", sortable: true },
        { key: "userId", label: "UserID", sortable: true },
        { key: "roomId", label: "Room", sortable: true },
        { key: "chatName", label: "發言者" },
        { key: "chatContent", label: "對話內容" },
        { key: "tokenP", label: "詢問Token", sortable: true },
        { key: "tokenT", label: "回答Token", sortable: true },
      ],

      currentPage: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0,

      costInputAmount: "",
      costOutputAmount: "",
      costTotalAmount: "",
      costCalculated: false,

      idleTimer: null,
    };
  },
  computed: {
    filteredNpcOptions() {
      if (!this.npcSearch) return this.npcOptions;
      const keyword = this.npcSearch.toLowerCase();
      return this.npcOptions.filter(
        (opt) =>
          opt.text.toLowerCase().includes(keyword) ||
          String(opt.value).toLowerCase().includes(keyword)
      );
    },
  },
  mounted() {
    this.fetchNPCOptions();
    this.resetIdleTimer();
    ["mousemove", "keydown", "click"].forEach((evt) => {
      window.addEventListener(evt, this._onUserActivity);
    });
  },
  beforeDestroy() {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    ["mousemove", "keydown", "click"].forEach((evt) => {
      window.removeEventListener(evt, this._onUserActivity);
    });
  },
  methods: {
    // ── 閒置登出 ──────────────────────────────────────
    _onUserActivity() {
      this.resetIdleTimer();
    },
    resetIdleTimer() {
      if (this.idleTimer) clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        alert("閒置超過 2 小時，請重新登入");
        localStorage.removeItem("session_token");
        localStorage.removeItem("admin_role");
        this.$router.replace("/login");
      }, 2 * 60 * 60 * 1000);
    },

    logout() {
      localStorage.removeItem("session_token");
      localStorage.removeItem("admin_role");
      this.$router.replace("/login");
    },

    async fetchNPCOptions() {
      this.isLoadingNPCs = true;
      try {
        const response = await axios.get(`${this.apiNetWork2}/getAllNPCMegaNames`);
        const { data } = response;
        this.npcOptions = (data || [])
          .filter((row) => row && row.name)
          .map((row) => ({
            value: row.name,
            text:
              row.showName && row.showName.trim() !== ""
                ? `${row.name} (${row.showName})`
                : row.name,
          }));
      } catch (err) {
        console.error("載入 NPC 名單失敗:", err);
        this.npcOptions = [];
      } finally {
        this.isLoadingNPCs = false;
      }
    },

    selectNpc(opt) {
      this.selectedNpcText = opt.text;
      this.chatDataQuery.gameId = opt.value;
      this.$refs.npcDropdown.hide(true);
    },

    async searchChatData(isNewSearch = false) {
      this.isLoadingChatData = true;
      if (isNewSearch) {
        this.chatDataSearched = false;
      }

      try {
        let startTimeISO = undefined;
        let endTimeISO = undefined;

        if (this.chatDataQuery.startTime) {
          startTimeISO = new Date(this.chatDataQuery.startTime).toISOString();
        }
        if (this.chatDataQuery.endTime) {
          endTimeISO = new Date(this.chatDataQuery.endTime).toISOString();
        }

        const requestData = {
          gameId: this.chatDataQuery.gameId || undefined,
          userId: this.chatDataQuery.userId || undefined,
          startTime: startTimeISO,
          endTime: endTimeISO,
          page: this.currentPage,
          pageSize: this.pageSize,
        };

        Object.keys(requestData).forEach((key) => {
          if (requestData[key] === undefined || requestData[key] === "") {
            delete requestData[key];
          }
        });

        const response = await axios.post(
          `${this.apiNetWork2}/queryMegaGPTRecord`,
          requestData
        );

        if (response.data) {
          let results = [];

          if (Array.isArray(response.data)) {
            results = response.data;
          } else {
            results = response.data.data || [];
          }

          // 前端時間過濾
          if (startTimeISO || endTimeISO) {
            results = results.filter((item) => {
              if (!item.timestamp) return true;
              const itemTime = new Date(item.timestamp).getTime();
              if (startTimeISO && itemTime < new Date(startTimeISO).getTime()) return false;
              if (endTimeISO && itemTime > new Date(endTimeISO).getTime()) return false;
              return true;
            });
          }

          this.chatDataResults = results;
          this.total = results.length;
          this.totalPages = Math.ceil(results.length / this.pageSize);
          this.chatDataSearched = true;

          if (isNewSearch) {
            if (this.chatDataResults.length > 0) {
              alert(`找到 ${this.total} 筆聊天記錄`);
            } else {
              alert("沒有找到符合條件的聊天記錄");
            }
          }
        }
      } catch (error) {
        console.error("查詢聊天數據失敗:", error);
        alert("查詢失敗，請檢查網路連線或聯絡管理員");
      } finally {
        this.isLoadingChatData = false;
      }
    },

    clearChatDataQuery() {
      this.chatDataQuery = { gameId: "", userId: "", startTime: "", endTime: "" };
      this.selectedNpcText = "";
      this.npcSearch = "";
      this.chatDataResults = [];
      this.chatDataSearched = false;
      this.currentPage = 1;
      this.total = 0;
      this.totalPages = 0;
      this.costInputAmount = "";
      this.costOutputAmount = "";
      this.costTotalAmount = "";
      this.costCalculated = false;
    },

    handlePageChange(page) {
      this.currentPage = page;
    },

    calculateManualCost() {
      const hasManualValue =
        this.costInputAmount !== "" || this.costOutputAmount !== "";

      if (!hasManualValue) {
        this.costTotalAmount = "";
        this.costCalculated = false;
        alert("請先輸入金額");
        return;
      }

      let totalP = 0;
      let totalT = 0;
      this.chatDataResults.forEach((item) => {
        totalP += parseFloat(this.parseTokenCal(item.tokenCal, "p")) || 0;
        totalT += parseFloat(this.parseTokenCal(item.tokenCal, "t")) || 0;
      });

      const inputPricePerM = Math.max(parseFloat(this.costInputAmount) || 0, 0);
      const outputPricePerM = Math.max(
        parseFloat(this.costOutputAmount) || 0,
        0
      );
      const inputCost = (totalP / 1000000) * inputPricePerM;
      const outputCost = (totalT / 1000000) * outputPricePerM;

      this.costTotalAmount = (inputCost + outputCost).toFixed(6);
      this.costCalculated = true;
    },

    async exportChatData(event) {
      if (this.total === 0) {
        alert("沒有數據可以匯出");
        return;
      }

      const button = event.target;
      const originalText = button.textContent;

      try {
        button.disabled = true;
        button.textContent = "匯出中...";

        let startTimeISO = undefined;
        let endTimeISO = undefined;

        if (this.chatDataQuery.startTime) {
          startTimeISO = new Date(this.chatDataQuery.startTime).toISOString();
        }
        if (this.chatDataQuery.endTime) {
          endTimeISO = new Date(this.chatDataQuery.endTime).toISOString();
        }

        const requestData = {
          gameId: this.chatDataQuery.gameId || undefined,
          userId: this.chatDataQuery.userId || undefined,
        };

        Object.keys(requestData).forEach((key) => {
          if (requestData[key] === undefined || requestData[key] === "") {
            delete requestData[key];
          }
        });

        const response = await axios.post(
          `${this.apiNetWork2}/queryMegaGPTRecord`,
          requestData
        );

        let allData = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            allData = response.data;
          } else if (response.data.data) {
            allData = response.data.data;
          }
        }

        if (startTimeISO || endTimeISO) {
          allData = allData.filter((item) => {
            if (!item.timestamp) return true;
            const itemTime = new Date(item.timestamp).getTime();
            if (startTimeISO && itemTime < new Date(startTimeISO).getTime()) return false;
            if (endTimeISO && itemTime > new Date(endTimeISO).getTime()) return false;
            return true;
          });
        }

        if (allData.length === 0) {
          alert("沒有符合條件的數據可以匯出");
          return;
        }

        const headers = ["時間", "遊戲ID", "用戶ID", "房間ID", "發言者", "對話內容"];
        const csvRows = [headers.join(",")];

        allData.forEach((item) => {
          const row = [
            item.timestamp || "",
            item.gameId || "",
            item.userId || "",
            item.roomId || "",
            item.chatName || "",
            `"${(item.chatContent || "").replace(/"/g, '""')}"`,
          ];
          csvRows.push(row.join(","));
        });

        const csvContent = "\uFEFF" + csvRows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `聊天記錄_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);

        alert(`CSV 文件已下載（共 ${allData.length} 筆記錄）`);
      } catch (error) {
        console.error("匯出 CSV 失敗:", error);
        alert("匯出失敗，請檢查網路連線或聯絡管理員");
      } finally {
        button.disabled = false;
        button.textContent = originalText;
      }
    },

    async exportExcelData(event) {
      if (this.total === 0) {
        alert("沒有數據可以匯出");
        return;
      }

      const button = event.target;
      const originalText = button.textContent;

      try {
        button.disabled = true;
        button.textContent = "匯出中...";

        let startTimeISO = undefined;
        let endTimeISO = undefined;

        if (this.chatDataQuery.startTime) {
          startTimeISO = new Date(this.chatDataQuery.startTime).toISOString();
        }
        if (this.chatDataQuery.endTime) {
          endTimeISO = new Date(this.chatDataQuery.endTime).toISOString();
        }

        const requestData = {
          gameId: this.chatDataQuery.gameId || undefined,
          userId: this.chatDataQuery.userId || undefined,
        };

        Object.keys(requestData).forEach((key) => {
          if (requestData[key] === undefined || requestData[key] === "") {
            delete requestData[key];
          }
        });

        const response = await axios.post(
          `${this.apiNetWork2}/queryMegaGPTRecord`,
          requestData
        );

        let allData = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            allData = response.data;
          } else if (response.data.data) {
            allData = response.data.data;
          }
        }

        if (startTimeISO || endTimeISO) {
          allData = allData.filter((item) => {
            if (!item.timestamp) return true;
            const itemTime = new Date(item.timestamp).getTime();
            if (startTimeISO && itemTime < new Date(startTimeISO).getTime()) return false;
            if (endTimeISO && itemTime > new Date(endTimeISO).getTime()) return false;
            return true;
          });
        }

        if (allData.length === 0) {
          alert("沒有符合條件的數據可以匯出");
          return;
        }

        const roomData = {};
        allData.forEach((item) => {
          const roomId = item.roomId || "未知房間";
          if (!roomData[roomId]) roomData[roomId] = [];
          roomData[roomId].push(item);
        });

        Object.keys(roomData).forEach((roomId) => {
          roomData[roomId].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        });

        const workbook = XLSX.utils.book_new();

        Object.keys(roomData).forEach((roomId) => {
          const sheetData = roomData[roomId].map((item, index) => ({
            序號: index + 1,
            時間: item.timestamp,
            遊戲ID: item.gameId || "",
            用戶ID: item.userId || "",
            發言者: item.chatName || "",
            對話內容: item.chatContent || "",
          }));

          const worksheet = XLSX.utils.json_to_sheet(sheetData);
          worksheet["!cols"] = [
            { wch: 6 },
            { wch: 20 },
            { wch: 15 },
            { wch: 15 },
            { wch: 12 },
            { wch: 50 },
          ];

          let sheetName = roomId.replace(/[\\/*?[\]:]/g, "_");
          if (sheetName.length > 31) sheetName = sheetName.substring(0, 31);

          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        });

        button.textContent = "生成 Excel 中...";
        const fileName = `聊天記錄_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        alert(
          `Excel 文件已下載（共 ${allData.length} 筆記錄，分為 ${Object.keys(roomData).length} 個房間）`
        );
      } catch (error) {
        console.error("匯出 Excel 失敗:", error);
        alert("匯出失敗，請檢查網路連線或聯絡管理員");
      } finally {
        button.disabled = false;
        button.textContent = originalText;
      }
    },

    formatTimestampISO(timestamp) {
      if (!timestamp) return "";
      try {
        return new Date(timestamp).toLocaleString("zh-TW", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      } catch {
        return timestamp;
      }
    },

    parseTokenCal(value, key) {
      if (value == null || value === "") return "";
      try {
        const obj = typeof value === "string" ? JSON.parse(value) : value;
        return obj[key] ?? "";
      } catch {
        return "";
      }
    },
  },
};
</script>

<style scoped>
.viewer-wrapper {
  min-height: 100vh;
  background-color: #f4f6f9;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
}

.viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

.viewer-title {
  margin: 0;
  font-size: 20px;
  font-weight: bold;
  color: #2c3e50;
}

.viewer-body {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.npc-dropdown .btn {
  text-align: left;
}
</style>
